/**
 * Banner categories — grouping the in-website banners for the listing.
 *
 * Each live banner carries a `context` tag (the site's `category_short`, a pipe-separated section
 * list like "CASINO" or "CASINO|SPORT"). The listing groups banners by that EXACT tag, so one section
 * appears per category combination actually present in the data ("Casino", "Casino & Sport", …) rather
 * than a fixed vocabulary. Each banner keeps the real backend `placement` it came from, so an edit
 * still targets the true run selector, not the display category.
 */
import type { CreativeItem, CreativePlacement } from './creative-api';

/** One banner paired with the real backend placement it came from. */
export interface CategorizedBanner {
  placement: string;
  item: CreativeItem;
}

/** A group of banners sharing the exact same site category tag. */
export interface BannerCategoryGroup {
  /** Normalised grouping key: the UPPERCASE tokens joined by `|`; '' for uncategorised. */
  key: string;
  /** The category tokens (UPPERCASE), e.g. `['CASINO']` or `['CASINO','SPORT']`; empty = uncategorised. */
  tokens: string[];
  banners: CategorizedBanner[];
}

/** Split a banner's `context` (its site category, e.g. "CASINO|SPORT") into UPPERCASE tokens. Handles
 *  the pipe the platform uses plus a slash/comma for safety; empty/absent → no tokens. */
export const parseCategoryTokens = (context?: string): string[] =>
  (context ?? '')
    .split(/[|/,]/)
    .map((token) => token.trim().toUpperCase())
    .filter(Boolean);

/**
 * Group live banners by their EXACT category tag — one section per combination present in the data.
 * Groups are ordered fewest-tags-first then alphabetically (so "Casino" precedes "Casino & Sport");
 * banners with no category tag fall into a trailing uncategorised group so none are ever hidden.
 */
export const groupBannersByCategory = (placements: CreativePlacement[]): BannerCategoryGroup[] => {
  const groups = new Map<string, BannerCategoryGroup>();
  for (const placement of placements) {
    for (const item of placement.items) {
      const tokens = parseCategoryTokens(item.context);
      const key = tokens.join('|');
      let group = groups.get(key);
      if (!group) {
        group = { key, tokens, banners: [] };
        groups.set(key, group);
      }
      group.banners.push({ placement: placement.placement, item });
    }
  }
  return Array.from(groups.values()).sort((a, b) => {
    if (a.tokens.length === 0) return 1; // uncategorised last
    if (b.tokens.length === 0) return -1;
    if (a.tokens.length !== b.tokens.length) return a.tokens.length - b.tokens.length;
    return a.key.localeCompare(b.key);
  });
};
